/** Every deployment this codebase knows how to build. */
export const APP_ENVS = ['puppet-interview', 'sabol', 'crest'] as const;

export type AppEnv = (typeof APP_ENVS)[number];

/**
 * `VITE_APP_ENV` is baked in at build time, so an unrecognised value is a misconfigured build.
 * Fail loudly rather than falling through to an `undefined` `API_BASE_URL`, which silently sent
 * every request to `"undefined/group/…"`. `vite.config.ts` catches this first; this is a backstop.
 */
function resolveAppEnv(): AppEnv {
    const value = import.meta.env.VITE_APP_ENV;

    if (APP_ENVS.includes(value as AppEnv)) return value as AppEnv;

    throw new Error(
        `VITE_APP_ENV is "${value ?? '(unset)'}", expected one of: ${APP_ENVS.join(', ')}. ` +
            `Start or build the app with one of the yarn scripts (e.g. yarn start-puppet).`,
    );
}

export const APP_ENV: AppEnv = resolveAppEnv();

// pulled in from mangamo, use at your own risk:
export const __DEV__ = import.meta.env.DEV;

export const __TEST__ = import.meta.env.MODE === 'test';

// Keyed by AppEnv so adding a deployment is a type error until its URL is filled in.
const PROD_API_BASE_URLS: Record<AppEnv, string> = {
    'puppet-interview': 'https://us-central1-puppet-interview.cloudfunctions.net/api',
    sabol: 'https://us-central1-sabol-12772.cloudfunctions.net/api',
    crest: 'https://us-central1-crest-sel-chat.cloudfunctions.net/api',
};

/** Cloud Functions emulator URLs. Not wired up — see the note on `API_BASE_URL`. */
export const LOCAL_API_BASE_URLS: Record<AppEnv, string> = {
    'puppet-interview': 'http://127.0.0.1:5002/puppet-interview/us-central1/api',
    sabol: 'http://localhost:5002/sabol-12772/us-central1/api',
    crest: 'http://localhost:5002/crest-sel-chat/us-central1/api',
};

/**
 * Always production, including from `yarn start-*`. Swap in `LOCAL_API_BASE_URLS[APP_ENV]`
 * to develop against the emulator suite.
 */
export const API_BASE_URL: string = PROD_API_BASE_URLS[APP_ENV];

/** Only the environments with a public signup page have a reCAPTCHA key. */
const RECAPTCHA_SITE_KEYS: Partial<Record<AppEnv, string>> = {
    'puppet-interview': '6Lcen5orAAAAANISV95XWiKDHrIvpV8inhc-GNIA',
    crest: '6LdFrSoqAAAAALJkUXg5xij6d50_nZ-697ElEwrR',
};

export const ReCAPTCHA_SITE_KEY: string | undefined = RECAPTCHA_SITE_KEYS[APP_ENV];
